import React, { useEffect, useMemo, useRef } from 'react'
import styled from 'styled-components'

import { useTranslation } from '@root/shared/localization'

import { OtpInputInlineInput } from './otp-input-inline-input'

export type OtpInputProps = {
  value: string | undefined
  onChange: (value: string | undefined) => void
  disabled?: boolean
  hasError?: boolean
}

const NUM_OF_INPUTS: number = 6
/**
 * @see https://github.com/keys78/react-otp-kit/blob/main/demo/src/components/PackageClone.tsx
 * This is a partial clone of the OtpInput component from the react-otp-kit package.
 */
export const OtpInputInline: React.FC<OtpInputProps> = ({
  value,
  onChange,
  disabled,
  hasError = false,
}) => {
  const { t } = useTranslation()

  const digits = useMemo((): Array<string> => {
    if (value === undefined) {
      return Array.from({ length: NUM_OF_INPUTS }).map(() => '')
    }
    return [
      ...value,
      ...Array.from({ length: NUM_OF_INPUTS - value.length }).map(() => ''),
    ].slice(0, NUM_OF_INPUTS)
  }, [value])

  const inputRefs = useRef<Array<HTMLInputElement | undefined>>(
    Array.from<HTMLInputElement | undefined>({
      length: NUM_OF_INPUTS,
    }).fill(undefined),
  )

  // Effect to focus the next empty input field (only when typing forward, not when deleting)
  useEffect(() => {
    const nextEmptyIndex = digits.findIndex((digit) => digit === '')
    if (nextEmptyIndex !== -1) {
      const currentlyFocusedIndex = inputRefs.current.findIndex(
        (ref) => ref === document.activeElement,
      )
      // Avoid refocusing the same element - can cause stuck/freeze on backspace
      if (currentlyFocusedIndex !== nextEmptyIndex) {
        inputRefs.current[nextEmptyIndex]?.focus()
      }
    }
  }, [digits])

  // Effect to focus the next empty input field
  useEffect(() => {
    if (hasError) {
      inputRefs.current.at(-1)?.focus()
    }
  }, [hasError])

  // Effect to handle autoFocus prop
  useEffect(() => {
    inputRefs.current[0]?.focus()
  }, [])

  //handling input changes for different types
  const handleChange = (
    index: number,
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const inputValue = event.target.value
    const newDigits = [...digits]

    // cmake sure only the last character is considered and it is a digit
    if (/^\d*$/.test(inputValue)) {
      newDigits[index] = inputValue.at(-1) ?? ''
    }

    const next = newDigits.join('')
    onChange(next === '' ? undefined : next)

    if (inputValue && index < digits.length - 1) {
      inputRefs.current[index + 1]?.focus()
    }
  }

  // Handle key down event
  const handleKeyDown = (
    index: number,
    event: React.KeyboardEvent<HTMLInputElement>,
  ) => {
    const { key } = event
    if (
      key === 'ArrowLeft' &&
      index > 0 &&
      event.target === document.activeElement
    ) {
      inputRefs.current[index - 1]?.focus()
      event.preventDefault()
    } else if (
      key === 'ArrowRight' &&
      index < digits.length - 1 &&
      event.target === document.activeElement
    ) {
      inputRefs.current[index + 1]?.focus()
      event.preventDefault()
    } else if (
      (key === 'Backspace' || key === 'Delete') &&
      event.target === document.activeElement
    ) {
      const isBackspace = key === 'Backspace'
      const isDelete = key === 'Delete'
      if (isBackspace && index > 0 && digits[index] === '') {
        // Clear previous cell and move focus there
        const newDigits = [...digits]
        newDigits[index - 1] = ''
        onChange(newDigits.join('') || undefined)
        inputRefs.current[index - 1]?.focus()
        event.preventDefault()
      } else if (
        isDelete &&
        index < digits.length - 1 &&
        digits[index] === ''
      ) {
        // Clear next cell and move focus there
        const newDigits = [...digits]
        newDigits[index + 1] = ''
        onChange(newDigits.join('') || undefined)
        inputRefs.current[index + 1]?.focus()
        event.preventDefault()
      } else if ((isBackspace || isDelete) && digits[index] !== '') {
        // Clear current cell (handled explicitly for controlled input reliability)
        const newDigits = [...digits]
        newDigits[index] = ''
        onChange(newDigits.join('') || undefined)
        event.preventDefault()
      }
    } else if (/^\d$/.test(key) && index < digits.length) {
      const newValue = key
      if (digits[index] !== newValue) {
        const newDigits = [...digits]
        newDigits[index] = newValue
        if (index < digits.length - 1) {
          inputRefs.current[index + 1]?.focus()
        }
        const next = newDigits.join('')
        onChange(next === '' ? undefined : next)
      }
      event.preventDefault()
    }
  }

  // Handle paste event
  const handlePaste = (event: React.ClipboardEvent<HTMLInputElement>) => {
    event.preventDefault()
    const clipboardData = event.clipboardData.getData('Text')
    if (!clipboardData) {
      return
    }

    const clipboardText = clipboardData.replaceAll(/\D/g, '') //
    if (!clipboardText) {
      return
    } // If nothing was pasted, we don't proceed

    const clipboardDigits = [...clipboardText]
    const newDigits = [...digits]
    for (const [index, digit] of clipboardDigits
      .slice(0, digits.length)
      .entries()) {
      newDigits[index] = digit
    }
    const next = newDigits.join('')
    onChange(next === '' ? undefined : next)

    // Focus on the last input field containing pasted digit
    const lastPastedIndex = newDigits.findIndex((digit) => digit === '')
    const isLastInputFilled = lastPastedIndex === -1
    if (isLastInputFilled) {
      inputRefs.current.at(-1)?.focus()
    } else {
      inputRefs.current[lastPastedIndex]?.focus()
    }
  }
  const handleClick = (
    e: React.MouseEvent<HTMLInputElement>,
    index: number,
  ) => {
    // Allow native focus behavior; only adjust focus when helpful.
    e.stopPropagation()
    const nextEmptyIndex = digits.findIndex((digit) => digit === '')
    let nextIndex = index
    if (nextEmptyIndex !== -1) {
      nextIndex = Math.min(index, nextEmptyIndex)
    }
    inputRefs.current[nextIndex]?.focus()
  }

  return (
    <Root>
      <Group>
        {digits.map((digit, index) => (
          <OtpInputInlineInput
            onClick={(e: React.MouseEvent<HTMLInputElement>) =>
              handleClick(e, index)
            }
            key={index}
            hasError={hasError}
            ref={(el) => {
              if (el) {
                inputRefs.current[index] = el
              }
            }}
            type="tel"
            value={digit}
            onChange={(e) => handleChange(index, e)}
            onKeyDown={(e) => handleKeyDown(index, e)}
            onPaste={handlePaste}
            maxLength={1}
            readOnly={index !== 0 && digits[index - 1] === ''}
            disabled={disabled}
            autoComplete="one-time-code"
            aria-label={`Digit ${index + 1}`}
          />
        ))}
      </Group>
      <PasteButton type="button">{t('common.paste')}</PasteButton>
    </Root>
  )
}

const Root = styled.div`
  display: grid;

  justify-content: center;
  align-items: center;
  justify-items: center;

  gap: 10px;
`

const Group = styled.div`
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: 6px;
  justify-content: center;
  width: 100%;
`

const PasteButton = styled.button`
  display: inline-flex;
  height: 24px;
  justify-content: center;
  align-items: center;

  gap: 6px;
  flex-shrink: 0;
  background-color: var(--backgroundA-4);
  color: var(--text-12);
  border: none;
  border-radius: 6px;
  padding: 0px 12px;

  font-size: 10px;
  font-weight: 500;
  line-height: 12px;

  cursor: pointer;

  transition: background-color 0.2s ease;

  &:hover {
    background-color: var(--backgroundA-5);
  }

  &:active {
    background-color: var(--backgroundA-6);
  }
`
